import type { CardMarketQuote } from '../types';
import { usableMarketPrice } from '../hooks/useMarketQuotes';

/** Empty input. `partial` is an in-progress draft such as "1.". */
export type PriceDraft = number | undefined | 'partial';

export function parsePriceDraft(text: string): PriceDraft {
  const trimmed = text.trim();
  if (trimmed === '') return undefined;
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return 'partial';
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : 'partial';
}

export function formatPriceBound(value: number | undefined): string {
  return value == null ? '' : String(value);
}

export function commitPriceBounds(minText: string, maxText: string, swap: boolean): {
  complete: boolean;
  priceMin?: number;
  priceMax?: number;
  minText: string;
  maxText: string;
} {
  const min = parsePriceDraft(minText);
  const max = parsePriceDraft(maxText);
  if (min === 'partial' || max === 'partial') {
    return { complete: false, minText, maxText };
  }
  let priceMin = min;
  let priceMax = max;
  let nextMinText = minText;
  let nextMaxText = maxText;
  if (swap && priceMin != null && priceMax != null && priceMin > priceMax) {
    [priceMin, priceMax] = [priceMax, priceMin];
    nextMinText = formatPriceBound(priceMin);
    nextMaxText = formatPriceBound(priceMax);
  }
  return { complete: true, priceMin, priceMax, minText: nextMinText, maxText: nextMaxText };
}

export function cardMatchesPriceBounds(
  price: number | null | undefined,
  min: number | undefined,
  max: number | undefined,
): boolean {
  if (min == null && max == null) return true;
  if (price == null || !Number.isFinite(price)) return false;
  if (min != null && price < min) return false;
  if (max != null && price > max) return false;
  return true;
}

export function filterCardsByPrice<T extends { id: string }>(
  cards: T[],
  bounds: { priceMin?: number; priceMax?: number },
  prices: Record<string, number> | undefined,
): T[] {
  if (bounds.priceMin == null && bounds.priceMax == null) return cards;
  return cards.filter((card) => cardMatchesPriceBounds(prices?.[card.id], bounds.priceMin, bounds.priceMax));
}

export function displayUnitPrice(
  quote: CardMarketQuote | null | undefined,
  displayCurrency: string,
  convert: (amount: number, sourceCurrency?: string) => number | null,
): number | null {
  const raw = usableMarketPrice(quote);
  if (raw == null || !quote) return null;
  const source = quote.currency || 'EUR';
  if (source === displayCurrency) return raw;
  return convert(raw, source);
}

export function displayUnitPrices(
  quotes: Record<string, CardMarketQuote | null | undefined>,
  displayCurrency: string,
  convert: (amount: number, sourceCurrency?: string) => number | null,
): Record<string, number> {
  const prices: Record<string, number> = {};
  for (const [id, quote] of Object.entries(quotes)) {
    const price = displayUnitPrice(quote, displayCurrency, convert);
    if (price != null) prices[id] = price;
  }
  return prices;
}
